import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { applyAuctionSnapshotToPublicAuction, type AuctionSnapshotEvent } from "../lib/auction-events";
import { Sentry } from "../lib/sentry";
import type { PublicAuction } from "../lib/types";
import { usePlatformAuth } from "../lib/use-platform-auth";

type AuctionsContextValue = {
  auctions: PublicAuction[];
  loading: boolean;
  error: string | null;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  applyAuctionEvent: (event: AuctionSnapshotEvent) => void;
  liveCount: number;
  scheduledCount: number;
};

const AuctionsContext = createContext<AuctionsContextValue | null>(null);

export function AuctionsProvider({ children }: { children: ReactNode }) {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { organizations, isPending, bootstrapLoading } = usePlatformAuth();
  const [auctions, setAuctions] = useState<PublicAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // The org slug comes straight from the URL, and AuctionsProvider mounts above
  // AppLayout's redirect guard. So a bad/partial URL like `/budrunder` (a page
  // segment mistakenly captured as the org slug) would fetch with an unknown
  // slug and 404 before AppLayout can redirect. Only fetch once we've confirmed
  // the slug is one of the user's organizations.
  const isKnownOrg = !!organizationSlug && organizations.some((o) => o.slug === organizationSlug);
  const authSettled = !isPending && !bootstrapLoading;

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    if (!isKnownOrg) {
      setAuctions([]);
      // If auth has settled and the slug still isn't a known org, the URL is
      // bad and AppLayout is about to redirect — stop the spinner. Otherwise
      // auth is still resolving; keep loading until orgs arrive (this callback
      // re-runs when isKnownOrg flips true).
      if (authSettled) setLoading(false);
      return;
    }

    if (!opts?.silent) setLoading(true);
    setError(null);

    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const url = new URL(`${api}/api/auctions`);
    url.searchParams.set("organizationSlug", organizationSlug);

    try {
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        const fetchError = new Error(`Kunne ikke laste auksjoner (${res.status})`);
        // 401/403 are expected transient auth states — e.g. the session
        // expired mid-request and AppLayout is redirecting to /auth/login.
        // Only report genuinely unexpected server failures (5xx); 4xx is noise.
        if (res.status >= 500) {
          Sentry.captureException(fetchError, {
            extra: { status: res.status, responseBody: body, url: url.toString() },
          });
        }
        throw fetchError;
      }
      setAuctions((await res.json()) as PublicAuction[]);
    } catch (err) {
      // A TypeError here is a network failure (offline, DNS, flaky mobile
      // connection), not an app bug — surface it in the UI but don't report it
      // to Sentry, where it would just be noise.
      setError(err instanceof Error ? err.message : "Ukjent feil");
    } finally {
      setLoading(false);
    }
  }, [organizationSlug, isKnownOrg, authSettled]);

  const applyAuctionEvent = useCallback((event: AuctionSnapshotEvent) => {
    setAuctions((current) =>
      current.map((auction) => applyAuctionSnapshotToPublicAuction(auction, event)),
    );
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Ref so the SSE handler can read current auctions without re-creating the callback
  const auctionsRef = useRef(auctions);
  auctionsRef.current = auctions;

  const connectSSE = useCallback(() => {
    if (!organizationSlug) return;

    eventSourceRef.current?.close();

    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const source = new EventSource(`${api}/api/auctions/events`, {
      withCredentials: true,
    });

    source.addEventListener("auction", (message) => {
      try {
        const event = JSON.parse((message as MessageEvent<string>).data) as AuctionSnapshotEvent;
        const known = auctionsRef.current.some((a) => a.id === event.auctionId);
        if (known) {
          applyAuctionEvent(event);
        } else {
          // New auction (e.g. just went live) — fetch the full list
          void refresh({ silent: true });
        }
      } catch {
        // Ignore malformed events and keep the stream open.
      }
    });

    source.onerror = () => {
      source.close();
      eventSourceRef.current = null;
      // Reconnect after a short delay
      setTimeout(connectSSE, 3000);
    };

    eventSourceRef.current = source;
  }, [applyAuctionEvent, organizationSlug, refresh]);

  useEffect(() => {
    connectSSE();
    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [connectSSE]);

  // Refetch data when tab becomes visible again
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void refresh({ silent: true });
        // Reconnect SSE if dead
        if (!eventSourceRef.current || eventSourceRef.current.readyState === EventSource.CLOSED) {
          connectSSE();
        }
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refresh, connectSSE]);

  const liveCount = useMemo(() => auctions.filter((a) => a.status === "live").length, [auctions]);
  const scheduledCount = useMemo(
    () => auctions.filter((a) => a.status === "scheduled").length,
    [auctions],
  );

  const value = useMemo<AuctionsContextValue>(
    () => ({ auctions, loading, error, refresh, applyAuctionEvent, liveCount, scheduledCount }),
    [auctions, loading, error, refresh, applyAuctionEvent, liveCount, scheduledCount],
  );

  return <AuctionsContext.Provider value={value}>{children}</AuctionsContext.Provider>;
}

export function useAuctions() {
  const context = useContext(AuctionsContext);
  if (!context) throw new Error("useAuctions must be used within AuctionsProvider");
  return context;
}
